import { useEffect, useState } from "react";
import { ModalActions, ModalTitle, ModalContent, Button, Typography, Modal, Link } from "@kaltura/ds-react-components";
import { getEntryUrl, postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { SharedMenuItemProps } from "@kaltura/mediaspace-shared-types";
import { useEventHandler, useLastValueRef } from "@kaltura/mediaspace-shared-hooks";
import {
    EntryMetadataStructure,
    EditFormContents,
    EditFormElement,
    useEditForm,
} from "@kaltura/mediaspace-shared-edit-entry";

export interface CloneKmeRoomProps extends SharedMenuItemProps {
    entryId: string;
    formData: EntryMetadataStructure;
    fieldsStructure: EditFormElement[];
    reloadMedia?: () => void;
    /**
     * Optional mode for the modal: "room" | "session"
     */
    mode?: "room" | "session";
    /**
     * The URL to call for cloning the room/session
     */
    cloneUrl: string;
    /**
     * The URL to call for checking clone progress
     */
    cloneProgressUrl: string;
}

export interface CloneKmeRoomModalProps extends CloneKmeRoomProps {
    isOpen: boolean;
}

const textsStore = {
    Failed_to_clone_the_room: {
        room: translate("Failed to clone the room"),
        session: translate("Failed to clone the session"),
    },
    Clone_room_is_ready: {
        room: translate("Clone room is ready"),
        session: translate("Clone session is ready"),
    },
    Clone_room_in_progress: {
        room: translate("Clone room in progress"),
        session: translate("Clone session in progress"),
    },
    Room_was_successfully_cloned: {
        room: translate("Room was successfully cloned."),
        session: translate("Session was successfully cloned."),
    },
    Go_to_the_new_room: {
        room: translate("Go to the new room"),
        session: translate("Go to the new session"),
    },
    Room_was_successfully_cloned_You_can_continue_editing_the_new_room_s_metadata_or_navigate_to_the_new_room: {
        room: translate(
            "Room was successfully cloned. You can continue editing the new room’s metadata or navigate to the new room."
        ),
        session: translate(
            "Session was successfully cloned. You can continue editing the new session’s metadata or navigate to the new session."
        ),
    },
    We_are_in_the_process_of_copying_all_your_room_assets_This_may_take_some_time_You_can_edit_the_new_room_s_metadata_in_the_meantime:
        {
            room: translate(
                "We are in the process of copying all your room assets. This may take some time. You can edit the new room’s metadata in the meantime."
            ),
            session: translate(
                "We are in the process of copying all your session assets. This may take some time. You can edit the new session’s metadata in the meantime."
            ),
        },
};

/**
 * A modal window that initiates cloning a KME room and allows to edit the cloned room's metadata.
 *
 * The component is designed to be rendered from a media action menu item.
 */
export const CloneKmeRoomModal = (props: CloneKmeRoomModalProps) => {
    const { isOpen, entryId, formData, fieldsStructure, onCloseMenu, reloadMedia, mode, cloneUrl, cloneProgressUrl } =
        props;
    const [newEntryId, setNewEntryId] = useState("");
    const [cloneProgress, setCloneProgress] = useState(0);

    const { showToast } = useToastsContext();

    const formHookProps = useEditForm({
        entryIds: [newEntryId],
        formData,
        fieldsStructure,
        onSubmit: (response) => {
            if (response.error) {
                showToast({ severity: "error", message: translate("Failed to update information") });
            }
            else {
                showToast({
                    severity: "success",
                    message: translate("Your information has been successfully updated."),
                });
            }
        },
        onClose: () => onCloseMenu?.(),
        closeAfterSubmit: false,
        onlyBasicData: true,
    });

    // Whether the form actions should be disabled because of processing an API call
    const processingRequest = !newEntryId || formHookProps.processing;
    // Was the async job of copying room assets fully finished
    const isFinished = cloneProgress === 100;

    /*
     * Keep the reference to the up-to-date version of onCloseMenu callback.
     * The reference is being reset to undefined after closing the modal
     * to prevent taking unwanted actions after finishing async job (see onModalClose).
     */
    const onCloseMenuRef = useLastValueRef(onCloseMenu);

    const getText = (key: keyof typeof textsStore) => textsStore[key][mode || "room"];

    const handleError = () => {
        showToast({ severity: "error", message: getText("Failed_to_clone_the_room") });
        onCloseMenuRef.current?.();
    };

    // Clone the room when opening the modal (when the user clicks the action item)
    const onModalOpen = useEventHandler(async () => {
        try {
            // Clone the room entry and start the async job of copying the assets
            const { success, entryId: newEntryId } = await postKmsData(cloneUrl, { entryId });
            if (!success) {
                handleError();
                return;
            }

            setNewEntryId(newEntryId);

            // Poll the status of the async jobs each 3 seconds until it finishes (successfully or with an error)
            // eslint-disable-next-line no-constant-condition
            while (true) {
                // Wait 3 seconds
                await new Promise<void>((resolve) => setTimeout(resolve, 3000));

                const { success, progress } = await postKmsData(cloneProgressUrl, { entryId: newEntryId });
                if (!success) {
                    handleError();
                    return;
                }

                setCloneProgress(progress);
                if (progress === 100) {
                    showToast({
                        severity: "success",
                        message: (
                            <>
                                {getText("Room_was_successfully_cloned")}{" "}
                                <Link href={getEntryUrl(newEntryId)}>{getText("Go_to_the_new_room")}</Link>
                            </>
                        ),
                    });
                    return;
                }
            }
        }
        catch (e: unknown) {
            console.error(e);
            handleError();
        }
    });

    const onModalClose = useEventHandler(() => {
        // Don't call onCloseMenu after the component was disposed
        onCloseMenuRef.current = undefined;

        // Reload the media list after disposing the component
        reloadMedia?.();
    });

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        onModalOpen();

        return () => onModalClose();
    }, [isOpen, onModalOpen, onModalClose]);

    return (
        <Modal open={isOpen} fullWidth={true} onClose={formHookProps.handleCancel}>
            <ModalTitle>
                <Typography variant={"h3"} component={"div"}>
                    {isFinished ? getText("Clone_room_is_ready") : getText("Clone_room_in_progress")}
                </Typography>
            </ModalTitle>
            <ModalContent>
                <Typography variant={"body1"} align={"center"} sx={{ color: (theme) => theme.kaltura.palette.tone2 }}>
                    {isFinished
                        ? getText(
                              "Room_was_successfully_cloned_You_can_continue_editing_the_new_room_s_metadata_or_navigate_to_the_new_room"
                          )
                        : getText(
                              "We_are_in_the_process_of_copying_all_your_room_assets_This_may_take_some_time_You_can_edit_the_new_room_s_metadata_in_the_meantime"
                          )}
                </Typography>

                <form
                    id={formHookProps.formId}
                    onSubmit={formHookProps.handleSubmitForm}
                    // Fix MUI bug for menu items
                    onKeyDown={(e) => e.stopPropagation()}
                >
                    <EditFormContents formData={formData} fieldsStructure={fieldsStructure} {...formHookProps} />
                </form>
            </ModalContent>
            <ModalActions>
                <Button
                    variant={"borderless"}
                    color={"secondary"}
                    disabled={processingRequest}
                    onClick={formHookProps.handleCancel}
                >
                    {translate("Close")}
                </Button>
                {isFinished && (
                    <Button variant={"borderless"} color={"secondary"} href={getEntryUrl(newEntryId)}>
                        {getText("Go_to_the_new_room")}
                    </Button>
                )}
                <Button
                    type={"submit"}
                    form={formHookProps.formId}
                    loading={processingRequest}
                    disabled={!formHookProps.canSaveForm}
                >
                    {translate("Save")}
                </Button>
            </ModalActions>
        </Modal>
    );
};
